import mapboxgl from 'mapbox-gl';
import {
  GL_COLOR_BLACK,
  GL_COLOR_WHITE,
  PADDOCK_COLORS,
  PADDOCK_FILL_LAYER_ID,
  PADDOCK_LABELS_LAYER_ID,
  PADDOCK_LABELS_SOURCE_ID,
  PADDOCK_LAYER_CONFIG,
  PADDOCK_OUTLINE_LAYER_ID,
  PADDOCK_SOURCE_ID,
} from 'constants/mapConstants';

// feature-state expression reused across fill, outline, and labels
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SELECTED: any = ['boolean', ['feature-state', 'selected'], false];

// isSelected property expression — used in label layer (layout + paint)
// Cannot use feature-state in layout or filter, so isSelected is stored as
// a GeoJSON feature property on the label point source instead.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IS_SELECTED: any = ['boolean', ['get', 'isSelected'], false];

/**
 * Creates the fill layer for paddocks.
 * Matches OpterraPhoenix mobile exactly:
 *   Selected:   fill-color rgba(255,255,255,0.7) × fill-opacity 0.7 → ~0.49 effective
 *   Unselected: fill-color transparent × fill-opacity 0.3 → no visible fill
 */
export function createPaddockFillLayer(): mapboxgl.LayerSpecification {
  return {
    id: PADDOCK_FILL_LAYER_ID,
    type: 'fill',
    source: PADDOCK_SOURCE_ID,
    paint: {
      'fill-color': ['case', SELECTED, PADDOCK_COLORS.selected, PADDOCK_COLORS.default],
      'fill-opacity': ['case', SELECTED, 0.7, 0.3],
    },
  };
}

/**
 * Creates the outline layer for paddocks.
 * White border; 3px when selected, 2px otherwise — matches OpterraPhoenix.
 */
export function createPaddockOutlineLayer(): mapboxgl.LayerSpecification {
  return {
    id: PADDOCK_OUTLINE_LAYER_ID,
    type: 'line',
    source: PADDOCK_SOURCE_ID,
    paint: {
      'line-color': PADDOCK_COLORS.outline,
      'line-width': ['case', SELECTED, 3, PADDOCK_LAYER_CONFIG.outlineWidth],
    },
  };
}

/**
 * Creates the labels layer for paddocks.
 * One label per polygon, centred on the turf centroid.
 * Always rendered (text-allow-overlap + text-ignore-placement).
 * Uses symbol-sort-key so the selected label always renders on top:
 *   feature-state cannot be used in layout properties, so `isSelected` is
 *   stored as a GeoJSON feature property on the label point source and
 *   updated via setData whenever selection changes.
 */
export function createPaddockLabelsLayer(): mapboxgl.LayerSpecification {
  return {
    id: PADDOCK_LABELS_LAYER_ID,
    type: 'symbol',
    source: PADDOCK_LABELS_SOURCE_ID,
    layout: {
      'text-field': [
        'format',
        ['get', 'name'],
        { 'font-scale': 1.0 },
        '\n',
        {},
        ['to-string', ['get', 'area']],
        { 'font-scale': 0.8 },
        ' ha',
        { 'font-scale': 0.8 },
      ],
      'text-size': ['interpolate', ['linear'], ['zoom'], 5, 8, 10, 10, 13, 14, 16, 16],
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-max-width': 12,
      // Selected label (sortKey 1) renders on top of unselected (sortKey 0)
      // when text-allow-overlap is true.
      'symbol-sort-key': ['case', IS_SELECTED, 1, 0],
    },
    paint: {
      'text-color': ['case', IS_SELECTED, GL_COLOR_WHITE, GL_COLOR_BLACK],
      'text-halo-color': ['case', IS_SELECTED, GL_COLOR_BLACK, GL_COLOR_WHITE],
      'text-halo-width': 1.5,
    },
  };
}

/**
 * Adds all paddock layers to the map in z-order:
 *   fill → outline → labels (selected label always on top via symbol-sort-key)
 */
export function addPaddockLayersToMap(
  map: mapboxgl.Map,
  beforeLayerId: string = 'road-path'
): void {
  map.addLayer(createPaddockFillLayer(), beforeLayerId);
  map.addLayer(createPaddockOutlineLayer(), beforeLayerId);
  map.addLayer(createPaddockLabelsLayer(), beforeLayerId);
}
